/**
 * AI Model catalog contracts — the code-side half of database-driven model
 * selection.
 *
 * The database records **which models exist** and **which model serves which
 * work** (`ai_models` + `ai_model_bindings`). This file records the other half:
 * **what each call site requires of a model**. That half belongs in code
 * because it changes when *our* call sites change, not when an operator makes a
 * catalog decision.
 *
 * It lives in `shared-types`, not in the backend, because exactly one
 * admissibility rule must be evaluated at three call sites — the runtime
 * resolver, the admin write path, and the admin UI's model picker. A
 * backend-only registry would force the UI to re-implement the rule in React,
 * which is the duplication the Specification pattern exists to prevent.
 *
 * This module is, and must stay, I/O-free and dependency-free.
 */
import { LLMProvider, LLMTaskType } from "./LLM.js";
import { MediaAnalysisReason } from "./Media.js";
// ============================================
// ENUMS
// ============================================
/** The unit a model's price is quoted in, and the unit the ledger counts. */
export var AiBillingUnit;
(function (AiBillingUnit) {
    AiBillingUnit["TOKENS"] = "TOKENS";
    AiBillingUnit["CHARS"] = "CHARS";
    AiBillingUnit["SECONDS"] = "SECONDS";
})(AiBillingUnit || (AiBillingUnit = {}));
/** A capability a model either has or does not have. */
export var AiModelCapability;
(function (AiModelCapability) {
    AiModelCapability["VISION"] = "VISION";
    AiModelCapability["TOOL_CALLING"] = "TOOL_CALLING";
    AiModelCapability["STREAMING"] = "STREAMING";
    AiModelCapability["STRUCTURED_OUTPUT"] = "STRUCTURED_OUTPUT";
    AiModelCapability["SPEECH_TO_TEXT"] = "SPEECH_TO_TEXT";
    AiModelCapability["TEXT_TO_SPEECH"] = "TEXT_TO_SPEECH";
})(AiModelCapability || (AiModelCapability = {}));
/** Lifecycle status of a catalog entry. */
export var AiModelStatus;
(function (AiModelStatus) {
    AiModelStatus["ACTIVE"] = "ACTIVE";
    AiModelStatus["DEPRECATED"] = "DEPRECATED";
})(AiModelStatus || (AiModelStatus = {}));
/** Scope a binding applies at. Organization beats platform. */
export var AiBindingScope;
(function (AiBindingScope) {
    AiBindingScope["PLATFORM"] = "PLATFORM";
    AiBindingScope["ORGANIZATION"] = "ORGANIZATION";
})(AiBindingScope || (AiBindingScope = {}));
/** Which process actually calls the provider for this task type. */
export var AiExecutionSite;
(function (AiExecutionSite) {
    AiExecutionSite["BACKEND"] = "BACKEND";
    AiExecutionSite["MEDIA_WORKER"] = "MEDIA_WORKER";
})(AiExecutionSite || (AiExecutionSite = {}));
// ============================================
// INVOCATION SETTINGS
// ============================================
/** The OpenAI endpoint a model is called through. */
export const OPENAI_APIS = ["chat_completions", "responses"];
/** Values of `reasoning.effort`; which ones a given model accepts is per model. */
export const OPENAI_REASONING_EFFORTS = [
    "none",
    "minimal",
    "low",
    "medium",
    "high",
    "xhigh",
    "max",
];
/** The efforts Anthropic accepts: OpenAI's list without `none` and `minimal`. */
export const ANTHROPIC_REASONING_EFFORTS = [
    "low",
    "medium",
    "high",
    "xhigh",
    "max",
];
export function isAnthropicReasoningEffort(value) {
    return ANTHROPIC_REASONING_EFFORTS.includes(value);
}
/** The invocation settings alone, out of any row or target that carries them. */
export function invocationSettingsOf(source) {
    return {
        openaiApi: source.openaiApi,
        reasoningEffort: source.reasoningEffort,
    };
}
export function sameInvocationSettings(a, b) {
    return a.openaiApi === b.openaiApi && a.reasoningEffort === b.reasoningEffort;
}
/**
 * Why a combination of invocation settings is refused, or null when it is fine.
 * One rule set for the admin write path and the admin form.
 *
 * - Only an OpenAI model has `openaiApi`.
 * - Only an OpenAI or Anthropic model has a reasoning effort, and Anthropic
 *   accepts neither `none` nor `minimal`.
 * - A reasoning effort other than `none` on an explicit `chat_completions`
 *   model is the 2026-08-09 outage shape (function tools plus effort over
 *   /v1/chat/completions), so it is never stored. An unset transport is not
 *   refused: the SDK routes some models to Responses by name, and the canary
 *   decides the rest.
 */
export function invocationSettingsViolation(provider, settings) {
    if (provider === LLMProvider.ANTHROPIC) {
        if (settings.openaiApi !== null) {
            return "openaiApi applies only to OpenAI models";
        }
        if (settings.reasoningEffort !== null &&
            !isAnthropicReasoningEffort(settings.reasoningEffort)) {
            return `An Anthropic reasoning effort is one of ${ANTHROPIC_REASONING_EFFORTS.join(", ")}`;
        }
        return null;
    }
    const hasSetting = settings.openaiApi !== null || settings.reasoningEffort !== null;
    if (hasSetting && provider !== LLMProvider.OPENAI) {
        return "openaiApi applies only to OpenAI models, and reasoningEffort only to OpenAI and Anthropic models";
    }
    if (settings.openaiApi === "chat_completions" &&
        settings.reasoningEffort !== null &&
        settings.reasoningEffort !== "none") {
        return "A reasoning effort other than 'none' needs the Responses API: OpenAI refuses function tools with reasoning on chat/completions";
    }
    return null;
}
/**
 * Per-variant output-token budgets for media analysis, carried verbatim from
 * the thirteen prompt definitions they replace. They live here rather than on
 * the prompt so a single site decides how many tokens a call may generate.
 */
const MEDIA_ANALYSIS_OUTPUT_TOKENS = {
    // Adaptive thinking tokens count against `max_tokens`, so the reasons that
    // answered in a few hundred tokens need room for the thinking too.
    [MediaAnalysisReason.ANIMAL_BODY_CONDITION_SCORE]: 8_000,
    [MediaAnalysisReason.ANIMAL_LAMENESS_CHECK]: 8_000,
    [MediaAnalysisReason.ANIMAL_HEALTH_INDICATORS]: 8_000,
    [MediaAnalysisReason.ANIMAL_BREED_IDENTIFICATION]: 8_000,
    [MediaAnalysisReason.PADDOCK_PASTURE_QUALITY]: 8_000,
    [MediaAnalysisReason.PADDOCK_WEED_DETECTION]: 8_000,
    [MediaAnalysisReason.PADDOCK_FENCE_CONDITION]: 8_000,
    [MediaAnalysisReason.PADDOCK_COVERAGE_ESTIMATION]: 8_000,
    [MediaAnalysisReason.EQUIPMENT_WEAR_CHECK]: 8_000,
    [MediaAnalysisReason.EQUIPMENT_DAMAGE_DETECTION]: 8_000,
    [MediaAnalysisReason.EQUIPMENT_MAINTENANCE_INDICATORS]: 8_000,
    // A Laudo can contain many glebas, each with a full set of readings. The
    // former 4,096-token ceiling truncated the forced tool call before it could
    // finish. Output is billed on tokens generated, not this ceiling. The worker
    // always builds its SDK client with an explicit timeout, so the SDK's
    // non-streaming size guard never applies: the real limit is that 60 s wall
    // clock, a higher ceiling buys nothing, and there is no streaming branch.
    [MediaAnalysisReason.SOIL_LAB_REPORT_EXTRACTION]: 16_000,
    [MediaAnalysisReason.TRANSACTION_RECEIPT_EXTRACTION]: 8_000,
    /**
     * DERIVED, not chosen — the one entry in this table that is not a free
     * constant. It must satisfy, at all times:
     *
     *   ANIMAL_IMPORT_CHUNK_ROWS × ANIMAL_IMPORT_TOKENS_PER_ROW × 2 ≤ budget ≤ 16_000
     *   ANIMAL_IMPORT_VISUAL_MAX_ROWS × ANIMAL_IMPORT_TOKENS_PER_ROW ≤ budget
     *
     * CSV chunks keep a 2x headroom: truncation is the failure the `max_tokens`
     * check exists to catch, output bills on ACTUAL tokens rather than on
     * `max_tokens`, so headroom is free and truncation is not. A PDF or photo is a
     * single call with the measured tail already in the estimate, so it runs at 1x.
     * Upper bound: ~16k is the non-streaming threshold; above it the SDK risks an
     * HTTP timeout on a non-streaming request and the design would have to grow a
     * streaming branch it does not have and does not want.
     *
     * At ~360 tok/row (measured): 20 × 360 × 2 = 14_400 and 40 × 360 = 14_400, so
     * 16_000. Whoever moves either row cap must move this in the same change; the
     * invariant is asserted by a unit test so the build fails instead of
     * production truncating.
     */
    [MediaAnalysisReason.ANIMAL_LIST_IMPORT_EXTRACTION]: 16_000,
};
/**
 * Named variants of CHAT. The default (no variant) is the orchestrator;
 * SUBAGENT binds the model its domain agents run on.
 */
export const ChatVariant = {
    SUBAGENT: "SUBAGENT",
};
/**
 * The registry. Keyed by `LLMTaskType`, so adding a task type is a compile
 * error until it declares what it requires of a model.
 */
export const LLM_TASK_PROFILES = {
    [LLMTaskType.CHAT]: {
        validVariants: [ChatVariant.SUBAGENT],
        allowsNoVariant: true,
        requiredCapabilities: [
            AiModelCapability.TOOL_CALLING,
            AiModelCapability.STREAMING,
        ],
        requiredBillingUnit: AiBillingUnit.TOKENS,
        supportedProviders: [LLMProvider.OPENAI],
        requestedOutputTokens: 4096,
        byokEligible: true,
        requiresContextCapacity: true,
        executionSite: AiExecutionSite.BACKEND,
    },
    [LLMTaskType.MEMORY_EXTRACTION]: {
        validVariants: null,
        requiredCapabilities: [],
        requiredBillingUnit: AiBillingUnit.TOKENS,
        supportedProviders: [LLMProvider.OPENAI],
        // Reasoning tokens count against this cap. At 100, gpt-6-luna at medium
        // effort spent the whole cap reasoning and returned no extraction.
        requestedOutputTokens: 1000,
        byokEligible: true,
        requiresContextCapacity: true,
        executionSite: AiExecutionSite.BACKEND,
    },
    [LLMTaskType.CONVERSATION_SUMMARY]: {
        validVariants: null,
        requiredCapabilities: [],
        requiredBillingUnit: AiBillingUnit.TOKENS,
        supportedProviders: [LLMProvider.OPENAI],
        // The notes take about 550 tokens; reasoning counts against the cap too.
        requestedOutputTokens: 1600,
        // Resolved like chat so the plan names the payer, but the worker makes no
        // call on an organization's own key: a new feature does not run on a
        // customer's key without their opt-in.
        byokEligible: true,
        requiresContextCapacity: true,
        executionSite: AiExecutionSite.BACKEND,
    },
    [LLMTaskType.MEDIA_ANALYSIS]: {
        validVariants: Object.values(MediaAnalysisReason),
        // TOOL_CALLING is not optional here: the worker asks for the result as a
        // tool call (forced, or `auto` for a row with an effort), so a model that
        // cannot call tools cannot serve this task at all.
        requiredCapabilities: [
            AiModelCapability.VISION,
            AiModelCapability.STRUCTURED_OUTPUT,
            AiModelCapability.TOOL_CALLING,
        ],
        requiredBillingUnit: AiBillingUnit.TOKENS,
        supportedProviders: [LLMProvider.ANTHROPIC],
        requestedOutputTokens: 1024,
        outputTokensByVariant: MEDIA_ANALYSIS_OUTPUT_TOKENS,
        byokEligible: false,
        requiresContextCapacity: false,
        executionSite: AiExecutionSite.MEDIA_WORKER,
    },
    [LLMTaskType.VOICE_SYNTHESIS]: {
        validVariants: null,
        requiredCapabilities: [AiModelCapability.TEXT_TO_SPEECH],
        requiredBillingUnit: AiBillingUnit.CHARS,
        supportedProviders: [LLMProvider.OPENAI, LLMProvider.ELEVENLABS],
        requestedOutputTokens: null,
        byokEligible: false,
        requiresContextCapacity: false,
        executionSite: AiExecutionSite.BACKEND,
    },
    [LLMTaskType.VOICE_TRANSCRIPTION]: {
        validVariants: null,
        requiredCapabilities: [AiModelCapability.SPEECH_TO_TEXT],
        requiredBillingUnit: AiBillingUnit.SECONDS,
        supportedProviders: [LLMProvider.OPENAI],
        requestedOutputTokens: null,
        byokEligible: false,
        requiresContextCapacity: false,
        executionSite: AiExecutionSite.BACKEND,
    },
};
/**
 * The one admissibility rule. Pure — no I/O, no clock, no database.
 *
 * Evaluated by the runtime resolver (before returning a bound model), by the
 * admin write path (before saving a binding), and by the admin UI (to filter
 * the model picker). Any second implementation of this predicate is a defect.
 */
export function isModelAdmissible(profile, model, variant) {
    if (model.status !== AiModelStatus.ACTIVE) {
        return { ok: false, reason: "MODEL_NOT_ACTIVE" };
    }
    if (!profile.supportedProviders.includes(model.provider)) {
        return { ok: false, reason: "UNSUPPORTED_PROVIDER" };
    }
    const isVoiceSynthesis = profile === LLM_TASK_PROFILES[LLMTaskType.VOICE_SYNTHESIS];
    const supportsRealtimeTokens = isVoiceSynthesis &&
        model.provider === LLMProvider.OPENAI &&
        model.model === "gpt-realtime-2.1-mini" &&
        model.billingUnit === AiBillingUnit.TOKENS;
    if (model.billingUnit !== profile.requiredBillingUnit &&
        !supportsRealtimeTokens) {
        return { ok: false, reason: "WRONG_BILLING_UNIT" };
    }
    const hasEveryCapability = profile.requiredCapabilities.every((capability) => model.capabilities.includes(capability));
    if (!hasEveryCapability) {
        return { ok: false, reason: "MISSING_CAPABILITY" };
    }
    if (profile.requiresContextCapacity && !hasContextCapacity(model)) {
        return { ok: false, reason: "MISSING_CONTEXT_CAPACITY" };
    }
    if (!isVariantValid(profile, variant)) {
        return { ok: false, reason: "UNKNOWN_VARIANT" };
    }
    return { ok: true };
}
/**
 * Both limits are present and usable. The `ai_models_limits_by_kind` CHECK only
 * guarantees NOT NULL for a generative row, so a zero or negative value is
 * refused here too — it is as unusable as a missing one.
 */
function hasContextCapacity(model) {
    return (isPositiveInteger(model.contextWindow) &&
        isPositiveInteger(model.maxOutputTokens));
}
function isPositiveInteger(value) {
    return value !== null && Number.isInteger(value) && value > 0;
}
/**
 * A task type with `validVariants === null` accepts only a null variant; one
 * with a variant list accepts only a member of it, plus null when the profile
 * `allowsNoVariant`. Both directions are wrong bindings, so both are rejected.
 */
function isVariantValid(profile, variant) {
    if (profile.validVariants === null) {
        return variant === null;
    }
    if (variant === null)
        return profile.allowsNoVariant === true;
    return profile.validVariants.includes(variant);
}
/**
 * The variants a binding row can actually be selected for.
 *
 * A null variant on a variant-taking task type is **not** "no variant" — it is
 * the catch-all rung of the resolver's precedence chain (org(variant) →
 * platform(variant) → org(taskType) → platform(taskType)), and it serves every
 * variant that has no more specific binding of its own. Feeding that literal
 * null to `isModelAdmissible` asks the wrong question: resolution validates
 * against the variant the CALLER requested, never against the binding's.
 */
export function variantsServedByBinding(profile, bindingVariant) {
    if (bindingVariant !== null)
        return [bindingVariant];
    if (profile.validVariants === null)
        return [null];
    return profile.allowsNoVariant
        ? [null, ...profile.validVariants]
        : profile.validVariants;
}
/**
 * Admissibility of a BINDING, as opposed to a single call.
 *
 * Composed from `isModelAdmissible` — still the one rule — applied to every
 * variant the binding can be selected for, because a catch-all is only safe if
 * it is admissible for all of them. A binding that names a variant reduces to a
 * single `isModelAdmissible` call, so a variant on a variantless task type and
 * an unknown variant both still surface as `UNKNOWN_VARIANT`.
 *
 * Use this wherever a binding ROW is validated (admin write path, admin picker,
 * deploy preflight); use `isModelAdmissible` where a single resolved call is.
 */
export function isBindingAdmissible(profile, model, bindingVariant) {
    for (const variant of variantsServedByBinding(profile, bindingVariant)) {
        const verdict = isModelAdmissible(profile, model, variant);
        if (!verdict.ok)
            return verdict;
    }
    return { ok: true };
}
/**
 * Output-token budget for a call, resolved through the per-variant override
 * map. An absent variant key falls back to the profile's value; a profile with
 * no meaningful token budget (speech) stays null regardless of variant.
 */
export function requestedOutputTokensFor(profile, variant) {
    if (variant !== null) {
        const override = profile.outputTokensByVariant?.[variant];
        if (override !== undefined) {
            return override;
        }
    }
    return profile.requestedOutputTokens;
}
//# sourceMappingURL=AIModel.js.map